import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollAreaViewport, ScrollBar } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Search, ChevronLeft } from "lucide-react";
import { cn, getCurrencySymbol } from "@/lib/utils";
import { useCompany } from "@/contexts/CompanyContext";
import { type DisputeUI } from "@/types/ui/disputes";

interface DisputeDetailsSidebarProps {
  readonly disputes: DisputeUI[];
  readonly isCollapsed?: boolean;
  readonly onToggleCollapse?: () => void;
}

export function DisputeDetailsSidebar({ 
  disputes, 
  isCollapsed = false,
  onToggleCollapse 
}: DisputeDetailsSidebarProps) {
  const { selectedCompany } = useCompany();
  const navigate = useNavigate();
  const { disputeId } = useParams<{ disputeId: string }>();
  const [searchQuery, setSearchQuery] = useState("");

  // Filter disputes based on search query
  const filteredDisputes = useMemo(() => {
    if (!searchQuery) return disputes;
    
    const query = searchQuery.toLowerCase();
    return disputes.filter(
      (d) =>
        d.disputeNumber.toLowerCase().includes(query) ||
        d.customer.toLowerCase().includes(query) ||
        d.amount.toString().includes(query) ||
        d.type.toLowerCase().includes(query)
    );
  }, [disputes, searchQuery]);

  const handleDisputeClick = (selectedId: string) => {
    navigate(`/disputes/all/${selectedId}`, {
      state: { from: "all" }
    });
  };

  const getBorderColor = (selectedId: string, currentId: string) => {
    // Only the selected dispute gets the teal border, others get light grey
    return selectedId === currentId 
      ? "border-l-teal-500" 
      : "border-l-gray-300 dark:border-l-gray-700";
  };

  const getPriorityVariant = (priority: string) => {
    switch (priority) {
      case "High":
        return "destructive";
      case "Medium":
        return "default";
      case "Low":
        return "secondary";
      default:
        return "default";
    }
  };

  return (
    <div className="relative h-full flex flex-col">
      {!isCollapsed && (
      <Card className="h-full border-r rounded-none flex flex-col shadow-md">
        <CardHeader className="pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Disputes</CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              className="h-8 w-8 rounded-full hover:bg-muted"
              title="Close sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>
          <div className="relative mt-2">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search disputes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0 flex-1 min-h-0">
          <ScrollArea className="h-full w-full">
            <ScrollAreaViewport className="h-full">
              <div className="space-y-1 px-3 pb-3">
                {filteredDisputes.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    {searchQuery ? "No disputes found" : "No disputes available"}
                  </div>
                ) : (
                  filteredDisputes.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => handleDisputeClick(d.id)}
                      className={cn(
                        "w-full text-left p-3 rounded-lg border-l-4 transition-all hover:bg-muted/50",
                        getBorderColor(disputeId || "", d.id),
                        d.id === disputeId
                          ? "bg-primary/10 shadow-sm"
                          : "bg-background hover:shadow-sm"
                      )}
                    >
                      <div className="space-y-1.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">
                              {d.disputeNumber}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {d.customer}
                            </p>
                          </div>
                          <Badge
                            variant={getPriorityVariant(d.priority)}
                            className="text-xs shrink-0"
                          >
                            {d.priority}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-red-600 dark:text-red-400">
                            {getCurrencySymbol(d.currencyCode ?? selectedCompany?.currency ?? 'GBP')}{d.amount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {d.type}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollAreaViewport>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
